import { faClose, faPlug } from "@fortawesome/free-solid-svg-icons"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import Button, { ButtonIcon } from "../../../components/Button/Button"
import Animate, { AnimationTypes } from "../../../components/Animate/Animate"
import ModalPortal from "../../../components/Modal/ModalPortal"
import { TitlePageResponsiveIconSmall } from "../../../components/Titles/Titles"
import { PopupHelpButton } from "../../../components/Help/PopupHelpButton"
import InputField from "../../../components/InputField/InputField"
import GetSpace from "../../../utils/space"
import THEME from "../../../configuration/Theme"
import { SIZE } from "../../../configuration/Sizes"
import defaultStyle from "../../../styles/defaultPageStyle.module.css"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"

export interface MetaTestSendPayload {
  toPhone: string
  notificationType: string
  messageBody: string
  tokenValues: Record<string, string>
}

interface MetaTestPopupProps {
  isOpen: boolean
  approvedTemplateOptions: Array<{ label: string; value: string; template: string }>
  initialNotificationType: string
  onClose: () => void
  onSendTestMessage: (payload: MetaTestSendPayload) => void
}

export const MetaTestPopup: React.FC<MetaTestPopupProps> = (props) => {
  const { t } = useTranslation("company")

  const [testPhone, setTestPhone] = useState<string>("")

  const [testNotificationType, setTestNotificationType] = useState<string>(props.initialNotificationType)

  const [testCustomerName, setTestCustomerName] = useState<string>(t("label_meta_test_default_customer"))

  const [testOrderId, setTestOrderId] = useState<string>("123")

  const [testOrderStatus, setTestOrderStatus] = useState<string>("Preparing")

  const [testOrderTotal, setTestOrderTotal] = useState<string>(t("label_meta_test_default_total"))

  const [testOrderDate, setTestOrderDate] = useState<string>(t("label_meta_test_default_date"))

  const [testOrderType, setTestOrderType] = useState<string>("Delivery")

  const [testPaymentStatus, setTestPaymentStatus] = useState<string>("WithInDebit")

  const [testDeliveryAddress, setTestDeliveryAddress] = useState<string>(t("label_meta_test_default_address"))

  const [testPrizeName, setTestPrizeName] = useState<string>(t("label_meta_test_default_prize"))

  useEffect(() => {
    if (!props.isOpen) {
      return
    }

    setTestNotificationType(props.initialNotificationType)
  }, [props.initialNotificationType, props.isOpen])

  useEffect(() => {
    if (props.approvedTemplateOptions.length === 0) {
      return
    }

    const existsInOptions = props.approvedTemplateOptions.some((option) => option.value === testNotificationType)
    if (!existsInOptions) {
      setTestNotificationType(props.approvedTemplateOptions[0].value)
    }
  }, [props.approvedTemplateOptions, testNotificationType])

  const selectedTemplate = useMemo(
    () =>
      props.approvedTemplateOptions.find((option) => option.value === testNotificationType)?.template ??
      props.approvedTemplateOptions[0]?.template ??
      t("label_meta_default_order_created_template"),
    [props.approvedTemplateOptions, t, testNotificationType],
  )

  const requiredTestFields = useMemo(
    () => ({
      customerName: selectedTemplate.includes("{{customerName}}"),
      orderId: selectedTemplate.includes("{{orderId}}"),
      orderStatus: selectedTemplate.includes("{{orderStatus}}"),
      orderTotal: selectedTemplate.includes("{{orderTotal}}"),
      orderDate: selectedTemplate.includes("{{orderDate}}"),
      orderType: selectedTemplate.includes("{{orderType}}"),
      paymentStatus: selectedTemplate.includes("{{paymentStatus}}"),
      deliveryAddress: selectedTemplate.includes("{{deliveryAddress}}"),
      prizeName: selectedTemplate.includes("{{prizeName}}"),
    }),
    [selectedTemplate],
  )

  const previewText = useMemo(() => {
    const replacements: Record<string, string> = {
      "{{customerName}}": testCustomerName,
      "{{orderId}}": testOrderId,
      "{{storeName}}": t("label_meta_test_default_store"),
      "{{orderStatus}}": testOrderStatus,
      "{{orderTotal}}": testOrderTotal,
      "{{orderDate}}": testOrderDate,
      "{{orderType}}": testOrderType,
      "{{deliveryAddress}}": testDeliveryAddress,
      "{{paymentStatus}}": testPaymentStatus,
      "{{prizeName}}": testPrizeName,
    }

    return Object.entries(replacements).reduce(
      (current, [token, value]) => current.split(token).join(value),
      selectedTemplate,
    )
  }, [
    selectedTemplate,
    t,
    testCustomerName,
    testDeliveryAddress,
    testOrderDate,
    testOrderId,
    testOrderStatus,
    testOrderTotal,
    testOrderType,
    testPaymentStatus,
    testPrizeName,
  ])

  if (!props.isOpen) {
    return null
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert}>
            <TitlePageResponsiveIconSmall
              title={t("label_meta_test_section_title")}
              iconColor={THEME.colors.iconBGColor}
              icon={faPlug}
            >
              <div className={`${defaultStyle.ButtonsActions} flex items-center gap-1`}>
                <PopupHelpButton
                  helpKey="pages.mystore.mycompany.metatestpopup.1"
                  variant="global"
                  title={t("helpPopup:global.metaTestPopup.title")}
                  description={t("helpPopup:global.metaTestPopup.description")}
                  bullets={t("helpPopup:global.metaTestPopup.bullets", { returnObjects: true }) as string[]}
                />

                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={props.onClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaTestPopup.button.propsOnClose"
                />
              </div>
            </TitlePageResponsiveIconSmall>

            <div className="p-2 max-h-[70vh] overflow-y-auto">
              {props.approvedTemplateOptions.length === 0 ? (
                <section className="rounded-lg border border-amber-300 bg-amber-50 p-3">
                  <p className="text-sm font-semibold text-amber-900">{t("label_meta_test_waiting_title")}</p>

                  <p className="mt-1 text-xs text-amber-800">{t("label_meta_no_approved_templates_for_test")}</p>

                  <ul className="mt-2 list-disc pl-4 space-y-1 text-[11px] text-amber-900">
                    <li>{t("label_meta_test_waiting_line_1")}</li>

                    <li>{t("label_meta_test_waiting_line_2")}</li>

                    <li>{t("label_meta_test_waiting_line_3")}</li>
                  </ul>
                </section>
              ) : (
                <>
                  <InputField
                    type="text"
                    placeholder={t("label_meta_test_phone")}
                    value={testPhone}
                    onChange={(e) => setTestPhone(e.target.value)}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaTestPopup.input.metaTestPhone"
                  />

                  <GetSpace top={8} />

                  <GenericDropDown
                    legend=""
                    initialState={testNotificationType}
                    data={props.approvedTemplateOptions.map((item) => ({ label: item.label, value: item.value }))}
                    handlerFuncion={(e) => setTestNotificationType(e.target.value)}
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaTestPopup.select.select"
                  />

                  <GetSpace top={8} />

                  <Animate type={AnimationTypes.slideUpIn} duration="1.2s">
                    <div className="mt-2 p-2 rounded-lg border border-slate-200 bg-white">
                      <p className="text-xs font-semibold text-slate-700">{t("label_meta_preview_title")}</p>

                      <p className="text-xs text-slate-600 mt-1 whitespace-pre-wrap">{previewText}</p>
                    </div>
                  </Animate>

                  <GetSpace top={8} />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {requiredTestFields.customerName && (
                      <InputField
                        type="text"
                        placeholder="{{customerName}}"
                        value={testCustomerName}
                        onChange={(e) => setTestCustomerName(e.target.value)}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaTestPopup.input.input"
                      />
                    )}

                    {requiredTestFields.orderId && (
                      <InputField
                        type="text"
                        placeholder="{{orderId}}"
                        value={testOrderId}
                        onChange={(e) => setTestOrderId(e.target.value)}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaTestPopup.input.input2"
                      />
                    )}

                    {requiredTestFields.orderStatus && (
                      <GenericDropDown
                        legend=""
                        initialState={testOrderStatus}
                        data={[
                          { label: t("label_meta_order_status_preparing"), value: "Preparing" },
                          { label: t("label_meta_order_status_in_delivery"), value: "InDelivery" },
                          { label: t("label_meta_order_status_delivered"), value: "Delivered" },
                          { label: t("label_meta_order_status_canceled"), value: "Canceled" },
                        ]}
                        handlerFuncion={(e) => setTestOrderStatus(e.target.value)}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaTestPopup.select.select2"
                      />
                    )}

                    {requiredTestFields.orderTotal && (
                      <InputField
                        type="text"
                        placeholder="{{orderTotal}}"
                        value={testOrderTotal}
                        onChange={(e) => setTestOrderTotal(e.target.value)}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaTestPopup.input.input3"
                      />
                    )}

                    {requiredTestFields.orderDate && (
                      <InputField
                        type="text"
                        placeholder="{{orderDate}}"
                        value={testOrderDate}
                        onChange={(e) => setTestOrderDate(e.target.value)}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaTestPopup.input.input4"
                      />
                    )}

                    {requiredTestFields.orderType && (
                      <GenericDropDown
                        legend=""
                        initialState={testOrderType}
                        data={[
                          { label: t("label_meta_order_type_delivery"), value: "Delivery" },
                          { label: t("label_meta_order_type_local"), value: "Local" },
                        ]}
                        handlerFuncion={(e) => setTestOrderType(e.target.value)}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaTestPopup.select.select3"
                      />
                    )}

                    {requiredTestFields.paymentStatus && (
                      <GenericDropDown
                        legend=""
                        initialState={testPaymentStatus}
                        data={[
                          { label: t("label_meta_payment_status_with_debit"), value: "WithInDebit" },
                          { label: t("label_meta_payment_status_without_debit"), value: "WithOutDebit" },
                        ]}
                        handlerFuncion={(e) => setTestPaymentStatus(e.target.value)}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaTestPopup.select.select4"
                      />
                    )}

                    {requiredTestFields.deliveryAddress && (
                      <InputField
                        type="text"
                        placeholder="{{deliveryAddress}}"
                        value={testDeliveryAddress}
                        onChange={(e) => setTestDeliveryAddress(e.target.value)}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaTestPopup.input.input5"
                      />
                    )}

                    {requiredTestFields.prizeName && (
                      <InputField
                        type="text"
                        placeholder="{{prizeName}}"
                        value={testPrizeName}
                        onChange={(e) => setTestPrizeName(e.target.value)}
                        descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaTestPopup.input.input6"
                      />
                    )}
                  </div>

                  <GetSpace top={8} />

                  <Button
                    sizeNumber={SIZE.buttonCloseIconSize}
                    label={t("label_meta_send_test_message")}
                    onClick={() =>
                      props.onSendTestMessage({
                        toPhone: testPhone,
                        notificationType: testNotificationType,
                        messageBody: previewText,
                        tokenValues: {
                          customerName: testCustomerName,
                          orderId: testOrderId,
                          storeName: t("label_meta_test_default_store"),
                          orderStatus: testOrderStatus,
                          orderTotal: testOrderTotal,
                          orderDate: testOrderDate,
                          orderType: testOrderType,
                          paymentStatus: testPaymentStatus,
                          deliveryAddress: testDeliveryAddress,
                          prizeName: testPrizeName,
                        },
                      })
                    }
                    type="primary"
                    size="small"
                    descriptionKey="interactionDescriptions:pages.myRestaurant.myCompany.metaTestPopup.button.metaSendTestMessage"
                  />
                </>
              )}
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
